import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { useCreateCommissionTier, useUpdateCommissionTier } from '@/hooks/use-mutations'
import type { CommissionTier } from '@/lib/api/types'

const formSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  slug: z.string().min(2, 'Slug deve ter pelo menos 2 caracteres'),
  position: z.number().min(1, 'Posição deve ser maior que 0'),
  minSalesCount: z.number().int().min(0, 'Mínimo de vendas deve ser >= 0'),
  commissionRate: z
    .number()
    .min(0, 'Taxa deve ser maior ou igual a 0')
    .max(1, 'Taxa deve ser menor ou igual a 1'),
  monthlySeedings: z.number().min(0, 'Quantidade de envios deve ser maior ou igual a 0'),
})

type FormValues = z.infer<typeof formSchema>

interface TierFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tier: CommissionTier | null
}

/**
 * Renders a modal dialog containing a form to create a new commission tier or edit an existing one.
 *
 * When `tier` is provided and the dialog is open, the form is populated with the tier's values.
 * When the dialog closes the form resets to its default values. Submitting the form triggers
 * a create or update mutation and closes the dialog on successful completion.
 *
 * @param open - Whether the dialog is visible
 * @param onOpenChange - Callback invoked with the new open state
 * @param tier - Optional commission tier to edit; when present the form operates in edit mode
 * @returns The dialog component with the tier form
 */
export function TierFormDialog({ open, onOpenChange, tier }: TierFormDialogProps) {
  const isEditing = !!tier

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      slug: '',
      position: 1,
      minSalesCount: 0,
      commissionRate: 0.1,
      monthlySeedings: 0,
    },
  })

  // We will need these custom mutations later. For now we use stubs or if they exist
  const createTier = useCreateCommissionTier()
  const updateTier = useUpdateCommissionTier()

  useEffect(() => {
    if (tier && open) {
      form.reset({
        name: tier.name,
        slug: tier.slug,
        position: tier.position,
        minSalesCount: tier.minSalesCount,
        commissionRate: tier.commissionRate,
        monthlySeedings: tier.monthlySeedings,
      })
    } else if (!open) {
      form.reset()
    }
  }, [tier, open, form])

  const onSubmit = (values: FormValues) => {
    if (isEditing) {
      updateTier.mutate(
        { id: tier.id, data: values },
        {
          onSuccess: () => {
            onOpenChange(false)
          },
        }
      )
    } else {
      createTier.mutate(values, {
        onSuccess: () => {
          onOpenChange(false)
        },
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Editar Tier' : 'Criar Novo Tier'}</SheetTitle>
          <SheetDescription>
            {isEditing
              ? 'Edite as regras deste tier de comissão.'
              : 'Adicione um novo nível de comissionamento para creators.'}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <SheetBody className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Tier</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Bronze" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slug</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: bronze" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="commissionRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Comissão (Rate)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="1"
                          placeholder="Ex: 0.15"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value))}
                        />
                      </FormControl>
                      <FormDescription>De 0.00 a 1.00</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="monthlySeedings"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Seedings p/ Mês</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="position"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Posição na Hierarquia</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                        />
                      </FormControl>
                      <FormDescription>1 é o tier mais baixo.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="minSalesCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Min. Vendas p/ Tier</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                        />
                      </FormControl>
                      <FormDescription>Vendas para atingir este tier.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </SheetBody>

            <SheetFooter>
              <ChonkyButton type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </ChonkyButton>
              <ChonkyButton type="submit" loading={createTier.isPending || updateTier.isPending}>
                {isEditing ? 'Salvar Mudanças' : 'Criar Tier'}
              </ChonkyButton>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
